import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { 
  Menu, 
  ShoppingCart, 
  User, 
  Globe, 
  ChevronDown,
  Monitor,
  Package,
  Cpu,
  Code,
  Wrench,
  Image,
  Phone,
  FileSpreadsheet,
  LayoutDashboard,
  LogOut
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map icon names to components
const ICON_MAP = {
  Monitor: Monitor,
  Package: Package,
  Cpu: Cpu,
  Code: Code,
  Wrench: Wrench,
  Image: Image,
  FileSpreadsheet: FileSpreadsheet,
  Phone: Phone,
  Menu: Menu
};

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navLinks, setNavLinks] = useState([]);

  // Fetch menu items from API
  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const res = await axios.get(`${API}/menu-items`);
        setNavLinks(res.data);
      } catch (error) {
        console.error('Error fetching menu items:', error);
        // Fallback to default items if API fails
        setNavLinks([
          { key: "rental", label_fr: "Location", label_en: "Rental", href: "/rental", icon: "Monitor", visible: true },
          { key: "sales", label_fr: "Vente", label_en: "Sales", href: "/sales", icon: "Package", visible: true },
          { key: "parts", label_fr: "Pièces", label_en: "Parts", href: "/parts", icon: "Cpu", visible: true },
          { key: "software", label_fr: "Logiciels", label_en: "Software", href: "/software", icon: "Code", visible: true },
          { key: "services", label_fr: "Services", label_en: "Services", href: "/services", icon: "Wrench", visible: true },
          { key: "realizations", label_fr: "Réalisations", label_en: "Realizations", href: "/realizations", icon: "Image", visible: true },
          { key: "soumission", label_fr: "Soumission", label_en: "Quote", href: "/soumission", icon: "FileSpreadsheet", visible: true },
          { key: "contact", label_fr: "Contact", label_en: "Contact", href: "/contact", icon: "Phone", visible: true },
        ]);
      }
    };
    fetchMenuItems();
  }, []);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr');
  };

  // Get label based on current language
  const getLabel = (item) => {
    return i18n.language === 'fr' ? item.label_fr : item.label_en;
  };

  // Get icon component
  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || Menu;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo-black.png" alt="RAQ Technologies" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                to={link.href}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  location.pathname === link.href
                    ? 'text-black bg-gray-100'
                    : 'text-gray-600 hover:text-black hover:bg-gray-50'
                }`}
                data-testid={`nav-${link.key}`}
              >
                {getLabel(link)}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className="text-gray-600 hover:text-black hover:bg-gray-100"
              data-testid="language-toggle"
            >
              <Globe className="h-5 w-5" />
              <span className="sr-only">Toggle language</span>
            </Button>

            {/* Cart */}
            <Link to="/cart" data-testid="cart-link">
              <Button variant="ghost" size="icon" className="relative text-gray-600 hover:text-black hover:bg-gray-100">
                <ShoppingCart className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-black text-white text-xs font-medium flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Button>
            </Link>

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-gray-600 hover:text-black hover:bg-gray-100 gap-2" data-testid="user-menu">
                    <User className="h-5 w-5" />
                    <span className="hidden sm:inline">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white border-gray-200">
                  <DropdownMenuItem asChild>
                    <Link to="/account" className="cursor-pointer" data-testid="account-link">
                      <User className="mr-2 h-4 w-4" />
                      {t('nav.account')}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin() && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer" data-testid="admin-link">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        {t('nav.admin')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-gray-200" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="login-link">
                <Button variant="default" size="sm" className="bg-black text-white hover:bg-gray-800">
                  {t('nav.login')}
                </Button>
              </Link>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden text-gray-600" data-testid="mobile-menu-btn">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-white border-gray-200">
                <nav className="flex flex-col gap-2 mt-8">
                  {navLinks.map((link) => {
                    const Icon = getIcon(link.icon);
                    return (
                      <Link
                        key={link.key}
                        to={link.href}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          location.pathname === link.href
                            ? 'text-black bg-gray-100'
                            : 'text-gray-600 hover:text-black hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                        {getLabel(link)}
                      </Link>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
